// The office app's shared stylesheets (apps/web/src/styles.css, loaded on every page; history/history.css,
// a customer's page and the edit sheets; and the frame's, every sheet Frame.tsx loads: shared/, account/,
// agents/, books/books.css and blueprint/ crumb, shell, inbox and the frame in blueprint.css) draw
// Riverstone's components, and Blueprint shows the screens not yet rebuilt through them
// (blueprint/blueprint.css, the bridge). Their measures are the same in both designs, so both token sets carry
// them under one set of names: each set points the name at its own base step where it has one, and
// writes the number where it hasn't. test/office.test.ts holds every length, weight and leading here
// equal in the two sets; only a colour may differ, as the bridge's colours do.
//
// Meaning (what a value is for), referenced by styles.css as var(--office-…):
// - office-space-N is N × 4 px, Riverstone's own step N (--space-N there); office-space-Nh is half a
//   step more (N × 4 + 2). office-nudge-N is N px, for the sub-grid offsets (a badge's 1 px, a 3 px gap).
// - office-type-* are Riverstone's type sizes that no shared --fs-* name gives in both designs (the
//   shared ones, --fs-small, --fs-card-sm, --fs-card, --fs-micro, --fs-h2, --fs-num, styles.css reads
//   as they are). Not named *-fs: they aren't Blueprint's type scale, which a -fs token promises.
// - office-fw-*, office-lh-*, office-line-*: weights, line heights and line widths.
// Components (what one of styles.css's components reads), in px, the same object in both sets.

export type OfficeSet = 'riverstone' | 'blueprint';

/** A meaning token's value in each set: [riverstone, blueprint]. */
const MEANING: Record<string, readonly [string, string]> = {
  // the 2 px grid the office's spacing sits on
  'office-nudge-1': ['1px', '1px'],
  'office-nudge-2': ['2px', '2px'],
  'office-nudge-3': ['3px', '3px'],
  'office-space-1': ['{space.1}', '{space.1}'],
  'office-space-1h': ['6px', '{space.2}'],
  'office-space-2': ['{space.2}', '{space.3}'],
  'office-space-2h': ['10px', '{space.4}'],
  'office-space-3': ['{space.3}', '{space.5}'],
  'office-space-3h': ['14px', '14px'],
  'office-space-4': ['{space.4}', '{space.6}'],
  'office-space-4h': ['18px', '18px'],
  'office-space-5': ['{space.5}', '{space.7}'],
  'office-space-5h': ['22px', '22px'],
  'office-space-6': ['{space.6}', '{space.8}'],
  'office-space-6h': ['26px', '26px'],
  // the page's gutter (docs/design/riverstone.md: --gutter, 28 px)
  'office-space-7': ['28px', '28px'],
  'office-space-7h': ['30px', '30px'],
  'office-space-8': ['{space.8}', '{space.9}'],
  'office-space-10': ['40px', '{space.10}'],
  'office-space-12': ['48px', '48px'],
  // the space before a pill's " · summary", which a box's first line drops (in ems, as the text is)
  'office-word-space': ['0.3em', '0.3em'],
  // a loading placeholder's line, at the height of the text that will arrive
  'office-ph-line': ['0.8em', '0.8em'],
  // the Agents place: a budget limit's field (in rems, as the field's own text), and the corner of a
  // question you asked in the chat, its tail on your side (Riverstone's --r-bubble is the answer's)
  'office-limit-w': ['9rem', '9rem'],
  'office-radius-bubble-mine': ['22px 22px 6px 22px', '22px 22px 6px 22px'],
  // a loading line's room, a line of text tall
  'office-ph-text-h': ['1.4em', '1.4em'],
  // type
  'office-type-badge': ['11px', '11px'], // a lane pill's badges and tags
  'office-type-chip': ['12.5px', '{fs.label}'], // chips, counts, the hours over the lanes
  'office-type-label': ['14px', '{fs.body}'], // a field's label, a list's lines
  'office-type-support': ['{fs.body}', '14.5px'], // supporting text: pills, notices, a stone's line
  'office-type-body': ['{fs.card-sm}', '{fs.body-lg}'], // the page's own text
  'office-type-icon': ['19px', '{fs.h3}'], // a glyph on an icon button
  'office-type-heading': ['{fs.card-lg}', '21px'], // a card's heading
  'office-type-ampm': ['0.5em', '0.5em'], // a.m. and p.m. after a big time
  'office-type-suffix': ['0.55em', '0.55em'], // a touch larger: the top bar clock's a.m./p.m., a pebble's unit
  'office-raise-suffix': ['0.8em', '0.8em'], // a stat's small unit, raised beside its figure
  'office-type-cents': ['0.62em', '0.62em'], // the books' cents, small beside the dollars
  'office-raise-cents': ['0.4em', '0.4em'], // and raised
  'office-type-lede': ['{fs.lede}', '16px'], // a summary line under a heading
  'office-type-table': ['13.5px', '{fs.table}'], // a timeline's times, a table's figures
  'office-tracking-title': ['-0.02em', '-0.02em'], // display sizes (docs/design/riverstone.md)
  'office-tracking-heading': ['-0.01em', '-0.01em'], // a panel's heading, a touch tighter
  'office-tracking-code': ['0.08em', '0.08em'], // a secret or a pairing code, spaced to read aloud
  'office-tracking-digits': ['0.1em', '0.1em'], // a one-time code typed digit by digit
  // the divisions' sheet on the board: a technician's home division, its picker's least and most width
  'office-home-pick-min': ['12em', '12em'],
  'office-home-pick-max': ['18em', '18em'],
  // weights
  'office-fw-regular': ['400', '{fw.body}'],
  'office-fw-semibold': ['600', '{fw.strong}'],
  'office-fw-bold': ['700', '{fw.total}'],
  'office-fw-heavy': ['800', '800'],
  // line heights: Riverstone's type roles' (theme.css --lh-*)
  'office-lh-flat': ['1', '1'], // big times
  'office-lh-title': ['1.05', '1.05'],
  'office-lh-tight': ['1.1', '{lh.tight}'],
  'office-lh-heading': ['1.15', '1.15'],
  'office-lh-label': ['1.2', '1.2'],
  'office-lh-snug': ['1.25', '{lh.snug}'],
  'office-lh-badge': ['1.3', '1.3'],
  'office-lh-body': ['1.45', '1.45'],
  // line widths
  'office-line-hair': ['1px', '{w.hair}'], // a table's and a list's rule
  'office-line-field': ['1.5px', '{w.icon}'], // a field's and a pill's edge, an outlined tag
  'office-line-ring': ['2px', '{w.focus}'], // a closed visit's ring, today's cell
  'office-line-strong': ['2.5px', '{w.strong}'], // on the way, late, a lane taking a drop
  'office-line-focus': ['3px', '{w.mark}'], // the focus outline, a ringing call's
  'office-line-mark': ['3px', '{w.mark}'], // a mark down an edge: a call's notes, the estimate picked
  // colours (as the bridge maps them in Blueprint)
  'office-hatch': [
    'repeating-linear-gradient(135deg, {color.surface-2} 0 {office-space-1h}, transparent {office-space-1h} {office-space-3})',
    'repeating-linear-gradient(135deg, {color.bp-bg} 0 {office-space-1h}, transparent {office-space-1h} {office-space-3})',
  ],
};

/** The office's meaning tokens as one set writes them. */
export const officeMeaning = (set: OfficeSet): Record<string, string> =>
  Object.fromEntries(Object.entries(MEANING).map(([k, v]) => [k, v[set === 'riverstone' ? 0 : 1]]));

/** What styles.css's components read, in px: the same in both sets. */
export const OFFICE_COMPONENTS: Record<string, number> = {
  // a field and the slim and big buttons
  'office-field-h': 46,
  'office-area-h': 92,
  'office-field-col-w': 190,
  'office-control-h-sm': 32,
  'office-btn-h-lg': 58,
  'office-icon': 20,
  // marks
  'office-avatar': 34,
  'office-avatar-sm': 26,
  'office-avatar-xs': 24,
  'office-logo': 38,
  'office-logo-head': 40,
  'office-logo-login': 56,
  'office-chevron': 7,
  'office-badge-pad-x': 7,
  // a rounded box inside a panel (the board's in-panel toast, a pause's reasons): 16 px in both designs,
  // where the bridge squares Riverstone's own radii in Blueprint
  'office-radius-inset': 16,
  // the frame: the places' pill bar, the move-off banner, a page, a narrow column, a hover card
  'office-places-pad': 5,
  'office-banner-h': 40,
  'office-page-max-w': 1200,
  'office-column-w': 440,
  'office-hovercard-w': 320,
  // a customer (history.css): the search, its results, the stat stones, the side column, the sheets
  'office-search-w': 560,
  'office-result-w': 260,
  'office-stat-w': 170,
  'office-pebble-w': 160,
  'office-cust-side-min-w': 240,
  'office-cust-side-w': 320,
  'office-sheet-w': 640,
  'office-sheet-w-wide': 720,
  'office-audio-w': 320,
  // Books setup: the steps beside the step at hand
  'office-steps-w': 320,
  'office-time-col-w-phone': 56,
  // the frame (blueprint/shell.css, shared/help.css): the search across the top, its list, the
  // person's menu, the tools' glyphs, and the room a dialog's title leaves for its Close
  'office-find-w': 300,
  'office-find-w-laptop': 200,
  'office-find-pop-min-w': 360,
  'office-me-pop-min-w': 240,
  'office-tool-icon': 18,
  'office-dialog-close-room': 96,
  // the dispatch board: the names column, the side column and the open visit's drawer
  'office-names-w': 180,
  'office-names-w-compact': 150,
  'office-hour-w-phone': 55,
  'office-side-w': 340,
  'office-drawer-w': 380,
  // a lane: its pills 64 px tall, a row of them every 72 px, 8 px in from its edges; compact 28 / 32 / 4
  'office-lane-pad': 8,
  'office-lane-pitch': 72,
  'office-lane-pill-h': 64,
  'office-lane-pad-compact': 4,
  'office-lane-pitch-compact': 32,
  'office-lane-pill-h-compact': 28,
  'office-pill-pad-x-short': 11,
  'office-tray-min-h': 120,
  // a drag on Riverstone's board (board-drag.css): a visit's end handles (wider on a touch screen), and
  // the note by the pointer, its width and corner
  'office-drag-edge-w': 9,
  'office-drag-edge-w-touch': 24,
  'office-drag-note-w': 320,
  'office-radius-note': 12,
  // a folded division on Riverstone's board (board-divisions.css): a technician's thin line, its corner
  'office-mini-line-h': 5,
  'office-radius-mini': 3,
  // Dispatch › Hours on Riverstone (hours/hours.css): the page's width
  'office-hours-max-w': 1100,
  'office-row-h': 36,
  'office-time-col-w': 72,
  'office-week-col-w': 96,
  // loading placeholders
  'office-ph-name-w': 96,
  'office-ph-stone-h': 96,
  'office-ph-stone-min-h': 64,
  'office-ph-pebble-w': 120,
  'office-ph-pebble-h': 31,
  // the company's pages and your sign-in (account/): the read-only banner's words, the narrow page,
  // Getting started's ticks, the authenticator's QR, a code's field and the recovery codes' column
  'office-banner-text-basis': 320,
  'office-page-narrow-w': 760,
  'office-step-tick': 28,
  'office-qr': 200,
  'office-code-field-w': 220,
  'office-recovery-code-w': 140,
  // notifications (shared/notifications.css): the bell's count, set up and out over its corner, the sheet, a time column
  'office-count': 22,
  'office-count-up': 5,
  'office-count-out': 7,
  'office-notes-w': 470,
  'office-ntime-w': 52,
  // the Agents place (agents/agents.css): its cards and avatars, a budget's meter, the on/off switch
  // (a 52 px pill in a 44 px target, 7 px above and below; its 24 px knob 3 px in), the activity's
  // time column, a step's number and a chat bubble
  'office-agent-card-w': 300,
  'office-agent-avatar': 40,
  'office-agent-avatar-sm': 28,
  'office-meter-h': 10,
  'office-switch-w': 52,
  'office-switch-pad-y': 7,
  'office-switch-knob': 24,
  'office-log-time-w': 76,
  'office-step-num-w': 24,
  'office-bubble-w': 640,
  // the books (books/books.css): the page, a card's least width, the aging bars, an entry's row
  // (its padding, its date and number columns), a bill line's words, a stock count's field
  'office-books-max-w': 1440,
  'office-books-card-min-w': 180,
  'office-bar-h': 6,
  'office-row-pad-y': 11,
  'office-entry-date-w': 90,
  'office-entry-num-w': 80,
  'office-bill-what-basis': 220,
  'office-qty-field-w': 110,
  // Blueprint's Inbox (blueprint/inbox.css): a compact field and text box, the unread dot, a queue
  // card's side, its placeholder's height, the warning meter's stripe, and the drawn switch (a 40 px
  // track, its 14 px knob 2 px in)
  'office-field-h-compact': 36,
  'office-area-h-compact': 80,
  'office-unread-dot': 7,
  'office-qcard-side-w': 260,
  'office-ph-qcard-h': 52,
  'office-meter-stripe': 5,
  'office-switch-w-bp': 40,
  'office-switch-knob-bp': 14,
  // the warning meter's stripes, drawn 1.25 px in both modes (not the rule, which is 1 px on the dark sheet)
  'office-stripe-w': 1.25,
  // Blueprint's frame (blueprint/blueprint.css): the top bar, the brand's mark, the person's icon, the
  // places' tabs overlapping the top's rule (1.25 px in both modes), and the drafting paper's grid
  'office-bp-top-h': 52,
  'office-brand-mark': 14,
  'office-me-icon': 22,
  'office-places-overlap': 1.25,
  'office-grid-cell': 16,
  'office-rail-w': 232, // the frame's rail (--bp-rail-w)
};
